import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface TemplateColumn {
  id: string;
  label: string;
  dataKey: string;
  unit?: string;
  width?: string;
  format?: 'text' | 'number' | 'date' | 'time' | 'datetime' | 'kw' | 'kwh';
}

interface TemplateSection {
  id: string;
  title: string;
  columns: TemplateColumn[];
}

interface PDFTemplate {
  reportTitle: string;
  reportSubtitle: string;
  leftLogo?: string;
  rightLogo?: string;
  companyFields: { label: string; value: string }[];
  showDateRange: boolean;
  dateRangeLabel: string;
  sections: TemplateSection[];
  showCheckedBy: boolean;
  showVerifiedBy: boolean;
  showGeneratedBy: boolean;
  footerText?: string;
  includeCharts?: boolean;
  primaryColor: string;
  borderStyle: 'single' | 'double' | 'none';
  fontSize: 'small' | 'medium' | 'large';
}

interface ReportTagContext {
  downloadedBy?: string;
  companyName?: string;
  userName?: string;
  plcName?: string;
  sectionName?: string;
  /** Overrides the "PLC"/"SECTION" labels in the header grid — e.g. 'METER' for an
   *  energy-meter report, so the same header layout reads correctly for any report
   *  type instead of only ever saying "PLC". Defaults to 'PLC'/'SECTION'. */
  plcLabel?: string;
  sectionLabel?: string;
}

// utility to replace placeholder tags like {clientName}, {date}
function replaceTags(str: string, tags: Record<string,string|number|undefined>) {
  if (!str) return '';
  return str.replace(/\{(\w+)\}/g, (_, k) => tags[k] !== undefined ? String(tags[k]) : '');
}

const extractUnitFromLabel = (label: string): string => {
  const text = String(label || '').trim();
  if (!text) return '';

  const parenMatch = text.match(/\(([^)]+)\)\s*$/);
  if (parenMatch?.[1]) return String(parenMatch[1]).trim();

  const squareMatch = text.match(/\[([^\]]+)\]\s*$/);
  if (squareMatch?.[1]) return String(squareMatch[1]).trim();

  return '';
};

const resolveColumnUnit = (col: TemplateColumn): string => {
  const explicitUnit = String(col.unit || '').trim();
  if (explicitUnit) return explicitUnit.toUpperCase();

  const labelUnit = extractUnitFromLabel(String(col.label || ''));
  if (labelUnit) return labelUnit.toUpperCase();

  if (col.format === 'kwh') return 'KWH';
  if (col.format === 'kw') return 'KW';
  return '';
};

export async function generateCustomPLCReport(
  liveData: any[],
  historicalData: any[],
  template: PDFTemplate,
  clientName: string,
  startDate?: Date,
  endDate?: Date,
  tagContext: ReportTagContext = {}
): Promise<Blob> {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  // helper tag values
  const now = new Date();
  const resolvedCompanyName = tagContext.companyName || clientName || '';
  const resolvedDownloadedBy = tagContext.downloadedBy || tagContext.userName || 'User';

  const tagValues: Record<string, string> = {
    clientName: clientName || '',
    companyName: resolvedCompanyName,
    downloadedBy: resolvedDownloadedBy,
    userName: resolvedDownloadedBy,
    date: now.toLocaleDateString(),
    time: now.toLocaleTimeString(),
    startDate: startDate ? startDate.toLocaleDateString() : '',
    endDate: endDate ? endDate.toLocaleDateString() : '',
  };

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let yPos = 12;
  const margin = 14;

  const signOffItems: string[] = [];
  if (template.showCheckedBy !== false) signOffItems.push('Checked by');
  if (template.showVerifiedBy !== false) signOffItems.push('Verified by');
  if (template.showGeneratedBy !== false) signOffItems.push('Generated by');

  // Reserve dedicated space so signature rows always stay at the page end.
  const footerMetaY = pageHeight - 8;
  const signatureTopY = pageHeight - 20;
  const tableBottomMargin = signOffItems.length > 0 ? 28 : 14;

  const fmtDate = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  // build sections automatically if template has none
  let sections: TemplateSection[] = template.sections || [];
  if (!sections || sections.length === 0) {
    const tagSet = new Set<string>();
    liveData.forEach(ld => {
      Object.keys(ld.values || {}).forEach(k => tagSet.add(k));
    });
    historicalData.forEach(hd => {
      Object.keys(hd.values || {}).forEach(k => tagSet.add(k));
    });
    const cols: TemplateColumn[] = Array.from(tagSet).map(tag => {
      const sample = liveData.find(ld => ld.values && ld.values[tag])?.values[tag]
                   || historicalData.find(hd => hd.values && hd.values[tag])?.values[tag];
      const format: TemplateColumn['format'] = typeof sample === 'number' ? 'number' : 'text';
      return { id: tag, label: tag, dataKey: tag, format };
    });
    sections = [{ id: 'auto', title: 'Tags', columns: cols }];
  }

  // ===== TOP LOGOS & TITLE =====
  if (template.leftLogo) {
    try {
      const format = template.leftLogo.includes('data:image/jpeg') ? 'JPEG' : 'PNG';
      doc.addImage(template.leftLogo, format, margin, yPos, 25, 15);
    } catch (e) { console.error('Left logo error', e); }
  }
  
  if (template.rightLogo) {
    try {
      const format = template.rightLogo.includes('data:image/jpeg') ? 'JPEG' : 'PNG';
      doc.addImage(template.rightLogo, format, pageWidth - margin - 25, yPos, 25, 15);
    } catch (e) { console.error('Right logo error', e); }
  }

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  const reportTitle = (template.reportTitle || 'REPORT').toUpperCase();
  const titleLines = doc.splitTextToSize(reportTitle, pageWidth - 100);
  titleLines.forEach((line: string, idx: number) => {
    const lw = doc.getTextWidth(line);
    doc.text(line, (pageWidth - lw) / 2, yPos + 6 + idx * 6);
  });
  
  yPos += 22;

  // ===== PROFESSIONAL HEADER GRID =====
  const resolvedPlcName = tagContext.plcName || '';
  const resolvedSectionName = tagContext.sectionName || '';
  const hasPlcInfo = !!(resolvedPlcName || resolvedSectionName);
  
  const headerBoxHeight = 26;
  doc.setDrawColor(0);
  doc.setLineWidth(0.2);
  
  // Outer border
  doc.rect(margin, yPos, pageWidth - 2 * margin, headerBoxHeight);
  
  // Vertical lines for a 4-column grid
  doc.line(margin + 38, yPos, margin + 38, yPos + headerBoxHeight);
  doc.line(pageWidth / 2, yPos, pageWidth / 2, yPos + headerBoxHeight);
  doc.line(pageWidth / 2 + 38, yPos, pageWidth / 2 + 38, yPos + headerBoxHeight);
  
  // Horizontal line between row 1 and row 2
  doc.line(margin, yPos + 8.5, pageWidth - margin, yPos + 8.5);

  // Horizontal line before date row
  const sepY = yPos + 17;
  doc.line(margin, sepY, pageWidth - margin, sepY);
  
  doc.setFontSize(9);
  
  // Left Column Labels
  doc.setFont('helvetica', 'bold');
  doc.text('COMPANY NAME', margin + 3, yPos + 6);
  doc.text('USER NAME', margin + 3, yPos + 13);
  doc.setFont('helvetica', 'normal');
  doc.text(resolvedCompanyName.toUpperCase(), margin + 41, yPos + 6);
  doc.text(resolvedDownloadedBy.toUpperCase(), margin + 41, yPos + 13);

  // Right column — PLC/Meter and Section (label text overridable per report type)
  const midX = pageWidth / 2 + 3;
  if (hasPlcInfo) {
    if (resolvedPlcName) {
      doc.setFont('helvetica', 'bold');
      doc.text((tagContext.plcLabel || 'PLC').toUpperCase(), midX, yPos + 6);
      doc.setFont('helvetica', 'normal');
      doc.text(resolvedPlcName.toUpperCase(), midX + 38, yPos + 6);
    }
    if (resolvedSectionName) {
      doc.setFont('helvetica', 'bold');
      doc.text((tagContext.sectionLabel || 'SECTION').toUpperCase(), midX, yPos + 13);
      doc.setFont('helvetica', 'normal');
      doc.text(resolvedSectionName.toUpperCase(), midX + 38, yPos + 13);
    }
  }
  
  if (startDate && endDate) {
    const dateRowY = yPos + 22;
    doc.setFont('helvetica', 'bold');
    doc.text('Report Start Date/Time', margin + 3, dateRowY);
    doc.setFont('helvetica', 'normal');
    doc.text(fmtDate(startDate), margin + 41, dateRowY);
    
    doc.setFont('helvetica', 'bold');
    doc.text('Report End Date/Time', midX, dateRowY);
    doc.setFont('helvetica', 'normal');
    doc.text(fmtDate(endDate), midX + 38, dateRowY);
  }
  
  yPos += headerBoxHeight + 6;

  // ===== DATA TABLE WITH MULTI-LEVEL HEADERS =====
  const head1: any[] = [{ content: 'Date\nTime', rowSpan: 3, styles: { halign: 'center', valign: 'middle' } }];
  const head2: any[] = [];
  const head3: any[] = [];

  sections.forEach(sec => {
    head1.push({ 
      content: sec.title.toUpperCase(), 
      colSpan: sec.columns.length, 
      styles: { halign: 'center', valign: 'middle' } 
    });
    sec.columns.forEach(col => {
      head2.push({ 
        content: col.label.toUpperCase(), 
        styles: { halign: 'center', valign: 'middle' } 
      });
      const unit = resolveColumnUnit(col);
      head3.push({ 
        content: unit, 
        styles: { halign: 'center', valign: 'middle' } 
      });
    });
  });

  const bodyData: any[] = [];
  historicalData.forEach(dp => {
    const timestamp = dp.timestamp || Date.now();
    const d = new Date(timestamp);
    const pad = (n: number) => String(n).padStart(2, '0');
    const dateStr = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}\n${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    
    const row: any[] = [dateStr];
    sections.forEach(sec => {
      sec.columns.forEach(col => {
        let v = dp[col.dataKey];
        if (v === undefined || v === null) {
          const plc = liveData.find(l => l.plcId === dp.plcId);
          if (plc?.values) {
            const key = col.dataKey.split('_').pop();
            if (key) v = plc.values[key];
          }
        }
        row.push(typeof v === 'number' ? v.toFixed(2) : (v ?? '—'));
      });
    });
    bodyData.push(row);
  });

  const totalPagesExp = '{total_pages_count_string}';

  autoTable(doc, {
    startY: yPos,
    head: [head1, head2, head3],
    body: bodyData,
    theme: 'grid',
    styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1, halign: 'center', valign: 'middle' },
    headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold', lineWidth: 0.2 },
    columnStyles: {
      0: { cellWidth: 18, halign: 'center' },
    },
    margin: { top: 20, left: margin, right: margin, bottom: tableBottomMargin },
    didDrawPage: (data) => {
      // Page Footer
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0);

      const resolvedFooterText = replaceTags(template.footerText || '', tagValues);
      const pgNum = `Page: ${data.pageNumber}   of   ${totalPagesExp}`;

      if (resolvedFooterText) {
        doc.text(resolvedFooterText, margin, footerMetaY);
      }
      doc.text(pgNum, pageWidth / 2, footerMetaY, { align: 'center' });
    }
  });

  // ===== SIGN-OFF SECTION =====
  if (signOffItems.length > 0) {
    const lastPage = doc.getNumberOfPages();
    doc.setPage(lastPage);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');

    const textToUnderscoreSpace = 24;

    if (signOffItems.length === 1) {
      const x = pageWidth - margin - 60;
      doc.text(`${signOffItems[0]} :`, x, signatureTopY);
      doc.text('____________________', x + textToUnderscoreSpace, signatureTopY);
      doc.text('Date :', x, signatureTopY + 6);
      doc.text('____________________', x + textToUnderscoreSpace, signatureTopY + 6);
    } else {
      signOffItems.forEach((label, index) => {
        let x = margin;
        if (index === signOffItems.length - 1) {
          x = pageWidth - margin - 60; 
        } else if (index > 0) {
          x = margin + ((pageWidth - 2 * margin - 60) / (signOffItems.length - 1)) * index;
        }
        doc.text(`${label} :`, x, signatureTopY);
        doc.text('____________________', x + textToUnderscoreSpace, signatureTopY);
        doc.text('Date :', x, signatureTopY + 6);
        doc.text('____________________', x + textToUnderscoreSpace, signatureTopY + 6);
      });
    }
  }

  if (typeof (doc as any).putTotalPages === 'function') {
    (doc as any).putTotalPages(totalPagesExp);
  }
  // Trigger download in browser with a sensible filename, then return the blob
  try {
    const safeName = (template.reportTitle || clientName || 'report').replace(/[^a-z0-9-_\.]/gi, '_') + '.pdf';
    // doc.save triggers browser download
    (doc as any).save(safeName);
  } catch (e) {
    // ignore save errors in non-browser environments
    console.warn('[PDF] doc.save() failed', e);
  }
  return doc.output('blob');
}
